import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, Check, Menu } from 'lucide-react';
import { BasketIcon } from './BasketIcon';
import { supabase } from '../supabaseClient';
import { UserContext } from '../UserContext';

const features = [
  {
    image: { src: "/images/personalized-learning.jpg", alt: "Relational Learning" },
    title: "Relational Learning",
    description: "See how familiar topics make difficult topics easier to study and remember.",
    points: [
      "Anchor to Known Concepts",
      "Create meaningful associations",
      "Each connection strengthens memory"
    ]
  },
  {
    image: { src: "/images/rotational-learning.jpg", alt: "Rotational Learning" },
    title: "Rotational Learning",
    description: "Review and reinforce memory of old topics while learning new ones. No separate review sessions needed",
    points: [
      "Seamless integration of related concepts",
      "Spaced repetition for optimal retention",
      "Cross-subject connections for deeper understanding"
    ]
  },
  {
    image: { src: "/images/reduced-study-volume.jpg", alt: "Reduced Study Volume" },
    title: "Reduced Study Volume",
    description: "Instead of memorizing separate topics, remember fewer topics based on core retrieval cues.",
    points: [
      "Create meaningful connections between topics",
      "Reduce redundancy in your study material",
      "Improve recall with interconnected knowledge"
    ]
  }
];

const FeatureShowcase = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToFeature = useCallback((index) => {
    setCurrentIndex((prevIndex) => {
      if (index < 0) return features.length - 1;
      if (index >= features.length) return 0;
      return index;
    });
  }, []);

  const nextFeature = useCallback(() => goToFeature(currentIndex + 1), [currentIndex, goToFeature]);
  const prevFeature = useCallback(() => goToFeature(currentIndex - 1), [currentIndex, goToFeature]);

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="relative pb-[56.25%]"> {/* 16:9 aspect ratio */}
        <img
          src={features[currentIndex].image.src}
          alt={features[currentIndex].image.alt}
          className="absolute top-0 left-0 w-full h-full object-cover"
        />
        <button
          onClick={prevFeature}
          className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-cranberry-700/80 rounded-full p-2 hover:bg-cranberry-600 transition-colors shadow-md"
          aria-label="Previous feature"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        <button
          onClick={nextFeature}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-cranberry-700/80 rounded-full p-2 hover:bg-cranberry-600 transition-colors shadow-md"
          aria-label="Next feature"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      </div>
      <div className="p-6 pt-2"> {/* Changed padding-top to 2 */}
        <div className="flex justify-center mb-3 space-x-2"> {/* Reduced margin-bottom to 3 */}
          {features.map((_, index) => (
            <button
              key={index}
              onClick={() => goToFeature(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentIndex ? "bg-cranberry-700" : "bg-neutral-200 hover:bg-neutral-400"
              }`}
              aria-label={`Go to feature ${index + 1}`}
            />
          ))}
        </div>
        <h3 className="text-2xl font-bold mb-4 text-neutral-900">{features[currentIndex].title}</h3>
        <p className="text-neutral-600">{features[currentIndex].description}</p>
      </div>
    </div>
  );
};

const TableWrapper = ({ children }) => (
  <div className="max-w-full -mx-4 sm:mx-0 overflow-x-auto shadow-sm">
    <div className="inline-block min-w-full align-middle">
      {children}
    </div>
  </div>
);

const PricingTable = () => (
  <div className="max-w-6xl mx-auto">
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-4 py-4 text-left text-lg font-semibold">Features</th>
            <th className="px-4 py-4 text-center">
              <div className="text-lg font-semibold">Free</div>
            </th>
            <th className="px-4 py-4 text-center">
              <div className="text-lg font-semibold">3 Months</div>
            </th>
            <th className="px-4 py-4 text-center">
              <div className="text-lg font-semibold">6 Months</div>
              <div className="mt-2">
                <span className="bg-cranberry-100 text-cranberry-700 px-3 py-1 rounded-full text-sm">
                  Recommended
                </span>
              </div>
            </th>
            <th className="px-4 py-4 text-center">
              <div className="text-lg font-semibold">Annual</div>
              <div className="mt-2">
                <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm">
                  Best Value
                </span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          <tr>
            <td className="px-4 py-4 text-gray-600">Price</td>
            <td className="px-4 py-4 text-center">$0</td>
            <td className="px-4 py-4 text-center">
              <div>$16.00</div>
            </td>
            <td className="px-4 py-4 text-center">
              <div>$26.50</div>
              <div className="text-sm text-emerald-600">Save 17%</div>
            </td>
            <td className="px-4 py-4 text-center">
              <div>$42.50</div>
              <div className="text-sm text-emerald-600">Save 33%</div>
            </td>
          </tr>
          <tr>
            <td className="px-4 py-4 text-gray-600">Monthly Cost</td>
            <td className="px-4 py-4 text-center">$0</td>
            <td className="px-4 py-4 text-center">$5.33</td>
            <td className="px-4 py-4 text-center">$4.42</td>
            <td className="px-4 py-4 text-center">$3.54</td>
          </tr>
          <tr>
            <td className="px-4 py-4 text-gray-600">Topics per Basket</td>
            <td className="px-4 py-4 text-center">Unlimited</td>
            <td className="px-4 py-4 text-center">Unlimited</td>
            <td className="px-4 py-4 text-center">Unlimited</td>
            <td className="px-4 py-4 text-center">Unlimited</td>
          </tr>
          <tr>
            <td className="px-4 py-4 text-gray-600">Mastery Filters</td>
            <td className="px-4 py-4 text-center">
              <X className="h-5 w-5 text-gray-400 mx-auto" />
            </td>
            <td className="px-4 py-4 text-center">
              <Check className="h-5 w-5 text-emerald-500 mx-auto" />
            </td>
            <td className="px-4 py-4 text-center">
              <Check className="h-5 w-5 text-emerald-500 mx-auto" />
            </td>
            <td className="px-4 py-4 text-center">
              <Check className="h-5 w-5 text-emerald-500 mx-auto" />
            </td>
          </tr>
          <tr>
            <td className="px-4 py-4 text-gray-600">Progress Insights</td>
            <td className="px-4 py-4 text-center">
              <X className="h-5 w-5 text-gray-400 mx-auto" />
            </td>
            <td className="px-4 py-4 text-center">
              <Check className="h-5 w-5 text-emerald-500 mx-auto" />
            </td>
            <td className="px-4 py-4 text-center">
              <Check className="h-5 w-5 text-emerald-500 mx-auto" />
            </td>
            <td className="px-4 py-4 text-center">
              <Check className="h-5 w-5 text-emerald-500 mx-auto" />
            </td>
          </tr>
          <tr>
            <td className="px-4 py-4 text-gray-600">Early Access Features</td>
            <td className="px-4 py-4 text-center">
              <X className="h-5 w-5 text-gray-400 mx-auto" />
            </td>
            <td className="px-4 py-4 text-center">
              <Check className="h-5 w-5 text-emerald-500 mx-auto" />
            </td>
            <td className="px-4 py-4 text-center">
              <Check className="h-5 w-5 text-emerald-500 mx-auto" />
            </td>
            <td className="px-4 py-4 text-center">
              <Check className="h-5 w-5 text-emerald-500 mx-auto" />
            </td>
          </tr>
          <tr>
            <td className="px-4 py-4 text-gray-600">Description</td>
            <td className="px-4 py-4 text-center text-sm text-gray-500">
              Get a taste of our unique learning approach
            </td>
            <td className="px-4 py-4 text-center text-sm text-gray-500">
              Perfect for short-term study goals with less than 5 subjects
            </td>
            <td className="px-4 py-4 text-center text-sm text-gray-500">
              Best for most students with 5+ subjects
            </td>
            <td className="px-4 py-4 text-center text-sm text-gray-500">
              Ideal if you want to take your time and save the most
            </td>
          </tr>
        </tbody>
      </table>

      <div className="bg-gray-50 px-4 py-4">
        <div className="text-center text-sm text-gray-600 max-w-2xl mx-auto">
          <span className="font-medium text-gray-900">Why choose the 6-month plan? </span>
          Best for most students with 5+ subjects. Save 17% compared to the 3-month plan while having enough time to build strong connections between topics.
        </div>
      </div>
    </div>
  </div>
);

const ComparisonTable = () => (
  <div className="overflow-x-auto">
    <table className="w-full border-collapse">
      <thead>
        <tr className="bg-[#FDF2F3]">
          <th className="border-2 border-[#0F172A] p-4 text-[#0F172A]">Features</th>
          <th className="border-2 border-[#0F172A] p-4 text-[#0F172A]">Basket</th>
          <th className="border-2 border-[#0F172A] p-4 text-[#0F172A]">Traditional Flashcards</th>
          <th className="border-2 border-[#0F172A] p-4 text-[#0F172A]">Note-Taking Apps</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="border-2 border-[#0F172A] p-4 text-[#475569]">Rotational Learning</td>
          <td className="border-2 border-[#0F172A] p-4"><Check className="text-[#059669] mx-auto" /></td>
          <td className="border-2 border-[#0F172A] p-4"><X className="text-[#DC2626] mx-auto" /></td>
          <td className="border-2 border-[#0F172A] p-4"><X className="text-[#DC2626] mx-auto" /></td>
        </tr>
        <tr>
          <td className="border-2 border-[#0F172A] p-4 text-[#475569]">Relational Learning</td>
          <td className="border-2 border-[#0F172A] p-4"><Check className="text-[#059669] mx-auto" /></td>
          <td className="border-2 border-[#0F172A] p-4"><X className="text-[#DC2626] mx-auto" /></td>
          <td className="border-2 border-[#0F172A] p-4"><X className="text-[#DC2626] mx-auto" /></td>
        </tr>
        <tr>
          <td className="border-2 border-[#0F172A] p-4 text-[#475569]">Reduced Study Volume</td>
          <td className="border-2 border-[#0F172A] p-4"><Check className="text-[#059669] mx-auto" /></td>
          <td className="border-2 border-[#0F172A] p-4"><X className="text-[#DC2626] mx-auto" /></td>
          <td className="border-2 border-[#0F172A] p-4"><X className="text-[#DC2626] mx-auto" /></td>
        </tr>
        <tr>
          <td className="border-2 border-[#0F172A] p-4 text-[#475569]">Active Recall</td>
          <td className="border-2 border-[#0F172A] p-4"><Check className="text-[#059669] mx-auto" /></td>
          <td className="border-2 border-[#0F172A] p-4"><Check className="text-[#059669] mx-auto" /></td>
          <td className="border-2 border-[#0F172A] p-4"><Check className="text-[#059669] mx-auto" /></td>
        </tr>
      </tbody>
    </table>
  </div>
);

const PrimaryButton = ({ onClick, children, className = '' }) => (
  <button 
    onClick={onClick}
    className={`bg-butter-500 text-neutral-900 px-6 py-2 rounded-xl border-2 border-neutral-900 font-medium hover:bg-butter-600 transition-colors duration-300 text-lg shadow-md ${className}`}
  >
    {children}
  </button>
);

const SecondaryButton = ({ onClick, children, className = '' }) => (
  <button 
    onClick={onClick}
    className={`bg-white text-neutral-900 px-6 py-2 rounded-xl border-2 border-neutral-900 font-medium hover:bg-neutral-50 transition-colors duration-300 text-lg shadow-md ${className}`}
  >
    {children}
  </button>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const [showDemo, setShowDemo] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [isChecking, setIsChecking] = useState(true);
  const { user } = useContext(UserContext);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (user) {
          navigate('/workspace');
        }
      } finally {
        setIsChecking(false);
      }
    };
    checkAuth();
  }, [user, navigate]);

  const handleGetStarted = () => {
    navigate('/auth', { state: { isSignup: true, email: signupEmail } });
  };

  const handleLogin = () => {
    navigate('/auth', { state: { isLogin: true } });
  };

  if (isChecking) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cranberry-700"></div>
      </div>
    );
  }

  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50 font-['Inter', sans-serif]">
      {/* Navbar */}
      <nav className="bg-cranberry-700 shadow-md fixed top-0 left-0 right-0 z-10 border-b-2 border-neutral-900">
        <div className="container mx-auto px-4 sm:px-6 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <BasketIcon color="#FFFFFF" className="w-6 h-6 mr-2" />
            <span className="text-xl font-bold text-white">Basket</span>
          </Link>
          
          {/* Mobile menu button */}
          <button className="md:hidden" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            <Menu className="text-white w-6 h-6" />
          </button>

          {/* Desktop menu */}
          <div className="hidden md:flex space-x-2 sm:space-x-4 items-center">
            <a href="#features" className="text-white hover:text-butter-200 transition-colors duration-200 text-sm">Features</a>
            <a href="#pricing" className="text-white hover:text-butter-200 transition-colors duration-200 text-sm">Pricing</a>
            <SecondaryButton onClick={handleLogin} className="text-sm py-1 px-3 sm:px-4">
              Log In
            </SecondaryButton>
            <PrimaryButton onClick={handleGetStarted} className="text-sm py-1 px-3 sm:px-4">
              Get Started
            </PrimaryButton>
          </div>

          {/* Mobile menu panel */}
          {isMenuOpen && (
            <div className="absolute top-full left-0 right-0 bg-cranberry-700 md:hidden p-4 border-b-2 border-neutral-900">
              <div className="flex flex-col space-y-4">
                <a href="#features" className="text-white hover:text-butter-200 transition-colors duration-200">Features</a>
                <a href="#pricing" className="text-white hover:text-butter-200 transition-colors duration-200">Pricing</a>
                <SecondaryButton onClick={handleLogin} className="w-full">Log In</SecondaryButton>
                <PrimaryButton onClick={handleGetStarted} className="w-full">Get Started</PrimaryButton>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20">
        {/* Hero Section */}
        <section className="flex flex-col lg:flex-row items-center mb-16 bg-white rounded-xl shadow-md overflow-hidden">
          <div className="w-full lg:w-1/2 p-4 sm:p-6 lg:p-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-4 leading-tight">
              Hard to Remember? Add a Trigger.
            </h1>
            <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
              Basket helps you make your hardest topics stick — by attaching them to ideas you already understand.
              It's fast, personal, and powerful. The next time you need that fact, it's right there.
            </p>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  className="flex-grow px-4 py-3 rounded-xl border-2 border-neutral-900 focus:border-cranberry-700 focus:ring-1 focus:ring-cranberry-700 outline-none text-lg"
                  aria-label="Email for signup"
                />
                <PrimaryButton onClick={handleGetStarted} className="w-full sm:w-auto text-xl py-3 whitespace-nowrap">
                  Get Started
                </PrimaryButton>
              </div>
              <p className="text-center text-sm text-neutral-600">No credit card required</p>
            </div>
          </div>
          <div className="w-full lg:w-1/2 p-4 sm:p-6 lg:p-8">
            <FeatureShowcase />
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="mb-8 sm:mb-12 lg:mb-16 bg-white rounded-xl border-2 border-[#0F172A] p-4 sm:p-6 lg:p-8 shadow-md">
          <h2 className="text-3xl font-bold text-center mb-8 text-[#0F172A]">Key Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-[#FDF2F3] p-6 rounded-xl border-2 border-[#0F172A] shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-[#0F172A]">Rotational Learning</h3>
              <p className="text-[#475569]">
                Review old topics naturally while learning new ones. No separate review sessions needed.
              </p>
            </div>
            
            <div className="bg-[#FDF2F3] p-6 rounded-xl border-2 border-[#0F172A] shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-[#0F172A]">Relational Learning</h3>
              <p className="text-[#475569]">
                Connect new concepts to familiar ones, making difficult topics easier to understand and remember.
              </p>
            </div>
            
            <div className="bg-[#FDF2F3] p-6 rounded-xl border-2 border-[#0F172A] shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-[#0F172A]">Reduced Study Volume</h3>
              <p className="text-[#475569]">
                Learn more with less material by focusing on connections between topics rather than memorizing each separately.
              </p>
            </div>
          </div>
        </section>

        {/* How Basket Works Section - Moved up */}
        <section id="how-it-works" className="mb-8 sm:mb-12 lg:mb-16 bg-white rounded-xl border-2 border-[#0F172A] p-4 sm:p-6 lg:p-8 shadow-md">
          <h2 className="text-3xl font-bold text-center mb-8 text-[#0F172A]">How Basket Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {[
              {
                title: "Step 1: Create Your Basket",
                description: "Start by creating a basket. This will serve as your retrieval cue for the items you will add later.",
                image: "/images/step1-create-basket.jpg"
              },
              {
                title: "Step 2: Find and Add Items",
                description: "Find or create a topic and add it to your basket.",
                image: "/images/step2-connect-learn.jpg"
              },
              {
                title: "Step 3: Review and Reduce",
                description: "Look up this retrieval cue every chance you get. Soon enough, all the items inside the basket will be effectively remembered as one topic.",
                image: "/images/step3-review-retain.jpg"
              }
            ].map((step, index) => (
              <div key={index} className="bg-[#FDF2F3] p-4 rounded-xl border-2 border-[#0F172A] flex flex-col transition-transform duration-300 hover:scale-105">
                <div className="relative w-full pb-[56.25%]"> {/* Added wrapper with 16:9 aspect ratio */}
                  <img 
                    src={step.image}
                    alt={step.title} 
                    className="absolute top-0 left-0 w-full h-full object-contain rounded-xl border-2 border-[#0F172A]"
                  />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-[#0F172A]">{step.title}</h3>
                <p className="text-[#475569]">{step.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Comparison Section - Moved down */}
        <section className="mb-8 sm:mb-12 lg:mb-16 bg-white rounded-xl border-2 border-[#0F172A] p-4 sm:p-6 lg:p-8 shadow-md">
          <h2 className="text-3xl font-bold text-center mb-8 text-[#0F172A]">How Basket Compares to Other Study Tools</h2>
          <ComparisonTable />
          <div className="mt-8 text-center">
            <p className="text-lg text-[#475569] mb-4 max-w-2xl mx-auto">
              Basket combines the best features of traditional study methods with innovative learning techniques, 
              providing a unique and effective study experience that adapts to your needs.
            </p>
            <PrimaryButton onClick={handleGetStarted}>
              Experience the Basket Difference
            </PrimaryButton>
          </div>
        </section>

        {/* Testimonial */}
        <section className="mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-3xl font-bold text-center mb-8 text-[#0F172A]">What Our Users Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* First Testimonial */}
            <div className="bg-[#FDF2F3] p-6 rounded-xl border-2 border-[#0F172A] shadow-md flex flex-col justify-between hover:shadow-lg transition-shadow duration-300">
              <div>
                <div className="mb-4">
                  <svg className="w-8 h-8 text-cranberry-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>
                <blockquote className="text-lg text-neutral-700 mb-4 leading-relaxed">
                  I was drowning in 1,700+ topics for my exam. Basket helped me group them into about <span className="font-semibold text-cranberry-700">300 theme-based connections</span>. Instead of memorizing each concept separately, I now understand how they relate. <span className="font-semibold text-cranberry-700">I went to my exam venue smirking! 😏 😆</span>
                </blockquote>
              </div>
              <div className="flex items-center mt-4 pt-4 border-t border-neutral-200">
                <div className="w-10 h-10 rounded-full bg-cranberry-200 flex items-center justify-center text-cranberry-700 font-bold">
                  KE
                </div>
                <div className="ml-3">
                  <p className="font-semibold text-neutral-900">Kian E.</p>
                  <p className="text-sm text-neutral-600">USMLE Step 1</p>
                </div>
              </div>
            </div>

            {/* Second Testimonial */}
            <div className="bg-[#FFF7E6] p-6 rounded-xl border-2 border-[#0F172A] shadow-md flex flex-col justify-between hover:shadow-lg transition-shadow duration-300">
              <div>
                <div className="mb-4">
                  <svg className="w-8 h-8 text-butter-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>
                <blockquote className="text-lg text-neutral-700 mb-4 leading-relaxed">
                  The most amazing thing about Basket is how I keep reviewing old topics naturally. While adding new topics to my <span className="font-semibold text-butter-700">'lemon' basket 🍋</span>, I automatically revisited epidural hematoma from 2 months ago. <span className="font-semibold text-butter-700">My early topics stayed fresh even during final week of revision! 🌟</span>
                </blockquote>
              </div>
              <div className="flex items-center mt-4 pt-4 border-t border-neutral-200">
                <div className="w-10 h-10 rounded-full bg-butter-200 flex items-center justify-center text-butter-700 font-bold">
                  NS
                </div>
                <div className="ml-3">
                  <p className="font-semibold text-neutral-900">Niraj S.</p>
                  <p className="text-sm text-neutral-600">PLAB 1</p>
                </div>
              </div>
            </div>

            {/* Third Testimonial */}
            <div className="bg-[#F0F9FF] p-6 rounded-xl border-2 border-[#0F172A] shadow-md flex flex-col justify-between hover:shadow-lg transition-shadow duration-300">
              <div>
                <div className="mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>
                <blockquote className="text-lg text-neutral-700 mb-4 leading-relaxed">
                  Biostatistics was like a foreign language until I used Basket. By connecting Statistic concepts to practical stuff in medicine <span className="font-semibold text-blue-600">everything started clicking! 💡</span>
                </blockquote>
              </div>
              <div className="flex items-center mt-4 pt-4 border-t border-neutral-200">
                <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-bold">
                  AJP
                </div>
                <div className="ml-3">
                  <p className="font-semibold text-neutral-900">Ana Joy P.</p>
                  <p className="text-sm text-neutral-600">PLE</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="mb-8 sm:mb-12 lg:mb-16 bg-white rounded-xl border-2 border-[#0F172A] p-4 sm:p-6 lg:p-8 shadow-md">
          <h2 className="text-3xl font-bold text-center mb-8 text-[#0F172A]">Simple, Transparent Pricing</h2>
          <PricingTable />
          <div className="text-center mt-8">
            <PrimaryButton onClick={handleGetStarted} className="text-xl">
              Try Free Plan
            </PrimaryButton>
            <p className="mt-4 text-sm text-[#475569]">No credit card required for free plan</p>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center mb-8 sm:mb-12 lg:mb-16 bg-[#741102] py-4 sm:py-6 lg:py-8 px-4 sm:px-6 lg:px-8 rounded-xl border-2 border-[#0F172A] shadow-md">
          <h2 className="text-4xl font-bold mb-4 text-white">Try it! You have nothing to lose but a lot to gain.</h2>
          <p className="text-xl text-[#F5CCD0] mb-6 max-w-3xl mx-auto">Join thousands of students who conquered their exams with Basket</p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-3 max-w-xl mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              value={signupEmail}
              onChange={(e) => setSignupEmail(e.target.value)}
              className="flex-grow px-4 py-3 rounded-xl border-2 border-neutral-900 focus:border-cranberry-700 focus:ring-1 focus:ring-cranberry-700 outline-none text-lg bg-white"
              aria-label="Email for signup"
            />
            <PrimaryButton onClick={handleGetStarted} className="text-xl px-8 py-3 whitespace-nowrap">
              Start Learning Now
            </PrimaryButton>
          </div>
          
          <p className="mt-4 text-sm text-[#F5CCD0]">Free plan available</p>
        </section>

        {/* Footer */}
        <footer className="text-center text-sm text-neutral-600 border-t-2 border-neutral-200 pt-6 pb-4">
          <p>&copy; 2024 Basket Learning. All rights reserved.</p>
          <div className="mt-4 space-x-6">
            <a href="#" className="hover:text-cranberry-700 transition-colors duration-200">About Us</a>
            <a href="#" className="hover:text-cranberry-700 transition-colors duration-200">FAQ</a>
            <Link to="/terms-of-service" className="hover:text-cranberry-700 transition-colors duration-200">Terms of Service</Link>
            <Link to="/privacy-policy" className="hover:text-cranberry-700 transition-colors duration-200">Privacy Policy</Link>
          </div>
        </footer>
      </div>

      {showDemo && (
        <div className="fixed inset-0 bg-[#0F172A] bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl border-2 border-[#0F172A] shadow-2xl relative max-w-4xl w-full">
            <button 
              onClick={() => setShowDemo(false)} 
              className="absolute top-4 right-4 text-[#475569] hover:text-[#0F172A] transition-colors duration-200"
            >
              <X size={32} />
            </button>
            <iframe 
              width="100%" 
              height="480" 
              src="https://www.youtube.com/embed/dQw4w9WgXcQ" 
              title="Basket Demo Video" 
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowFullScreen
              className="rounded-xl border-2 border-[#0F172A]"
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
